import React from 'react'

import { CaretDownOutlined } from '@ant-design/icons'
import _ from 'lodash'

import localization from './localization'
import { useTranslation } from 'react-i18next'
import { addLocalizations } from 'providers/Localization/i18n'

import themeStyles from './styles'
import { useTheme } from 'contexts/ThemeProvider'

const checkRange = (min, max, value) => {
  return value > max ? 'upper' : value < min ? 'lower' : 'within'
}

/**
 * A component for displaying a range color scale with a pointer indicating the current value.
 * @param {object} props - The props for the component
 * @param {number} props.thresholdMaxValue - The maximum threshold value
 * @param {number} props.thresholdMinValue - The minimum threshold value
 * @param {number} props.currentValue - The current value to be indicated on the range color scale
 */
const TestResultBarChart = props => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const thresholdMaxValue = _.get(props, 'thresholdMaxValue')
  const thresholdMinValue = _.get(props, 'thresholdMinValue')
  const currentValue = _.get(props, 'currentValue')
  const overRangeFlag = checkRange(
    thresholdMinValue,
    thresholdMaxValue,
    currentValue
  )

  const determinePointerPosition = () => {
    const stepSize =
      Math.abs(_.subtract(thresholdMaxValue, thresholdMinValue)) / 100
    let offset = _.subtract(currentValue, thresholdMinValue) / stepSize
    if (offset < -50) {
      offset = -50
    }
    if (offset > 150) {
      offset = 150
    }
    return `calc(${offset / 2 + 25}% )`
  }

  return (
    <div style={styles.rangeColorScale}>
      <div style={styles.pointer(determinePointerPosition())}>
        <div style={styles.currentValue(overRangeFlag)}>
          <strong style={styles.value}>{currentValue}</strong>
        </div>
        <CaretDownOutlined style={{ color: theme.approxBlack }} />
      </div>
      <div style={styles.rangeBar}>
        <div style={styles.belowRangeArea} />
        <div>
          <div style={styles.thresholdValue}>{thresholdMinValue}</div>
        </div>
        <div style={styles.withinRange} />
        <div>
          <div style={styles.thresholdValue}>{thresholdMaxValue}</div>
        </div>
        <div style={styles.aboveRange} />
      </div>
    </div>
  )
}

const RangeColorScale = ({ result, range, units, low, high }) => {
  const { i18n } = useTranslation()
  addLocalizations(i18n, localization)
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const isEmptyRange = value => _.isNil(value) || value === ''
  try {
    if (_.isNil(result)) {
      return null
    }
    if (
      _.isNaN(parseFloat(result)) ||
      _.isEmpty(range) ||
      isEmptyRange(low) ||
      isEmptyRange(high) ||
      low === high
    ) {
      return <p style={styles.stringLabel}>{`${result} ${units}`}</p>
    }
    const normalizedRange = _.trim(_.replace(range, units, ''))
    let [thresholdMinValue, thresholdMaxValue] = _.split(
      normalizedRange,
      ' - '
    ).map(_.toNumber)
    thresholdMinValue = thresholdMinValue || low
    thresholdMaxValue = thresholdMaxValue || high
    return (
      <div>
        <TestResultBarChart
          {...{ thresholdMinValue, thresholdMaxValue, currentValue: result }}
        />
      </div>
    )
  } catch {
    return null
  }
}

export default RangeColorScale
